import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Avatar, Box, Button, Container, CssBaseline, Grid, LinearProgress, Link, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Image from 'mui-image';
import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TabPanel from '../components/TabPanel';
import config from '../config';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import MfaCodeStep from './MfaCodeStep';
import MfaEnrollStep from './MfaEnrollStep';
import RecoveryCodesStep, { ACCOUNT_SECURITY_PATH, RecoveryCodesLeftStep } from './RecoveryCodesStep';
import { TENANT_CLOSED_TEXT, clearSignOutReason, peekSignOutReason } from './signOutReason';

import { ContextStore } from '../store/contextStore';

function Copyright() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            {'Copyright © '}
            <Link color="inherit" href="https://curatedplanet.com/">
                Curated Planet
            </Link>{' '}
            {new Date().getFullYear()}
        </Typography>
    );
}

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    tabPanel: {
        minWidth: "400px",
    }
}));

const AuthMethodAuth0 = (props) => {
    const { loginWithRedirect, logout, isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
    const [localIsLoading, setLocalIsLoading] = React.useState(false);
    const [redirecting, setRedirecting] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { finalizeLogin } = useContext(ContextStore);
    const classes = useStyles();
    const theme = useTheme();

    const onAuth0LoginClick = () => {
        loginWithRedirect({ authorizationParams: {
            // redirect_uri: props.data.callback_url || window.location.origin,
        }});
    }

    const onAuth0LogoutClick = () => {
        logout({ logoutParams: { returnTo: window.location.origin } });
    }

    const verifyLogin = async () => {
        setLocalIsLoading(true);
        const token = await getAccessTokenSilently();
        const host = config.domainOverride || window.location.host;
        try {
            const data = {
                host: host,
                idx: props.idx,
                token: token,
                client_id: props.data.client_id,
            };
            const res = await fetch(`${config.apiUrl}/oauth-verification/`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });
            const body = await res.json();
            // console.log('auth0:verifyLogin', res.status, body);
            if ([200, 201].indexOf(+res.status) >= 0) {
                setRedirecting(true);
                finalizeLogin(body, props.intendedPath || '/');
            } else if ([400, 404].indexOf(+res.status) >= 0 && !!body?.message) {
                setError(body.message);
            } else {
                console.log('auth0:verifyLogin: returned error', res.status, body);
                setError("An error occurred while verifying your login. Please try again in a few minutes. If it doesn't help, please contact support.");
            }
            // if (!active) { return; }
        } catch(err) {
            // if (!active) { return; }
            console.log('auth0:verifyLogin: caught error', err);
        }
        finally {
            setLocalIsLoading(false);
        }
    };

    const onAuth0ContinueClick = () => {
        verifyLogin();
    }

    React.useEffect(() => {
        if (!isAuthenticated || !user) { return; }
        verifyLogin();
    }, [user])

    // console.log('auth0', isLoading, isAuthenticated, user);

    if (isLoading) {
        return (<Box mt={theme.spacing(4)}><LinearProgress color="primary" />Proceeding with authentication...</Box>);
    }
    if (isAuthenticated) {
        return (<Box mt={theme.spacing(4)}>
            <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item xs={2} container alignItems="center" justifyContent="center">{ user.picture ? <Avatar alt={user.name} src={user.picture} /> : <Avatar>{user.name[0]}</Avatar> }</Grid>
                <Grid item xs={10}>
                    <Typography>
                        <strong>{user.name}</strong><br/>
                        {user.email}
                    </Typography>
                </Grid>
            </Grid>
            { localIsLoading || redirecting ? <LinearProgress color="primary" /> : <>
                { error ? <Typography color="error" sx={{ marginTop: theme.spacing(2) }}>{error}</Typography> : <>
                    <Button type="button" sx={{ marginTop: "20px" }} fullWidth variant="contained" color="primary" className={classes.submit} onClick={onAuth0ContinueClick}>
                        Continue as {user.name}
                    </Button>
                </> }
            </> }
            <Button type="button" sx={{ marginTop: "20px" }} fullWidth variant="outlined" color="primary" className={classes.submit} onClick={onAuth0LogoutClick}>
                Log in as another user
            </Button>

        </Box>);
    } else {
        return (<Box mt={theme.spacing(4)}>
            <Button type="button" fullWidth variant="contained" color="primary" className={classes.submit} onClick={onAuth0LoginClick}>
                Sign In With {props.data.name || 'Auth0'}
            </Button>
        </Box>);
    }
  }

// The e-mail to prefill: `?login_hint=` (the self-signup create answers a sign_in_url carrying the new
// admin's e-mail, backend plan 2026-10-17-dashboard-self-signup-invite-links §8.1).
export function loginHintFrom(search) {
    const hint = new URLSearchParams(search || '').get('login_hint');
    return hint ? hint.trim() : '';
}

const AuthMethodLocal = ({ intendedPath, onSecondStep, loginHint = '' }) => {
    const { login, error, errorMessage } = useContext(ContextStore);

    const classes = useStyles();
    const usernameRef = React.useRef(null);
    const passwordRef = React.useRef(null);
    const signInBtRef = React.useRef(null);
    const [forgotOpen, setForgotOpen] = React.useState(false);
    const [forgotEmail, setForgotEmail] = React.useState('');

    const onForgotClick = () => {
        setForgotEmail(usernameRef.current ? usernameRef.current.querySelector('input').value : '');
        setForgotOpen(true);
    };

    const errorProps = error && errorMessage ? { error: true, helperText: errorMessage } : {};

    const getLoginInfo = () => {
        return {
            username: usernameRef.current.querySelector('input').value,
            password: passwordRef.current.querySelector('input').value,
        };
    };

    const handleOnChange = (ev) => {};

    // A 202 (two-step sign-in) hands SignIn its next step; anything else stays here (login() says why).
    const handleSignIn = async (ev) => {
        const loginInfo = getLoginInfo();
        const next = await login(loginInfo, intendedPath);
        if (next && onSecondStep) {
            onSecondStep(next);
        }
    };

    return (
        <>
        <form className={classes.form} noValidate>
            <TextField {...errorProps} variant="outlined" margin="normal" required fullWidth id="email" label="Email Address" name="email" autoComplete="email" autoFocus={!loginHint} defaultValue={loginHint} onChange={handleOnChange} ref={usernameRef} />
            <TextField variant="outlined" margin="normal" required fullWidth name="password" label="Password" type="password" id="password" autoComplete="current-password" autoFocus={!!loginHint} onChange={handleOnChange} ref={passwordRef} />
            <Button type="button" fullWidth variant="contained" color="primary" className={classes.submit} ref={signInBtRef} onClick={handleSignIn}>
                Sign In
            </Button>
            <Box sx={{ textAlign: 'right' }}>
                <Link component="button" type="button" variant="body2" onClick={onForgotClick}>
                    Forgot password?
                </Link>
            </Box>
        </form>
        <ForgotPasswordDialog open={forgotOpen} initialEmail={forgotEmail} onClose={() => setForgotOpen(false)} />
        </>
    );
};

const AuthMethod = (props) => {
    if (props.data.backend === "auth0") {
        return AuthMethodAuth0(props);
    } else {
        return <AuthMethodLocal intendedPath={props.intendedPath} onSecondStep={props.onSecondStep} loginHint={props.loginHint} />;
    }
};

// The tenant card's 403 for a closed tenant: its words (the backend's `detail`, else ours), or null for any other 403.
async function tenantClosedText(res) {
    const body = await res.json().catch(() => null);
    if (!body || body.code !== 'tenant_closed') {
        return null;
    }
    return (typeof body.detail === 'string' && body.detail) || TENANT_CLOSED_TEXT;
}

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export function SignIn() {
    const history = useHistory();
    const location = useLocation();
    const { loading, finalizeLogin } = useContext(ContextStore);
    const classes = useStyles();
    const [initData, setInitData] = React.useState(null);
    // A closed tenant (403 tenant_closed on the tenant card, apiv2/dashboard_auth.openapi.yaml): the backend's neutral
    // words, final -- no retry, no sign-in methods, and no company name or logo (the answer carries none).
    const [closedText, setClosedText] = React.useState(null);
    // Two-step sign-in, after the password: {kind: 'mfa' | 'enroll' | 'codes' | 'codes_left', …}. Shown in place
    // of the sign-in methods, under the same tenant card, and finishing on the same intended path.
    const [step, setStep] = React.useState(null);
    // Why the dashboard just signed this tab out, if it said (./signOutReason): shown once.
    const [signOutReason] = React.useState(peekSignOutReason);
    React.useEffect(() => { clearSignOutReason(); }, []);
    const [, setInitError] = React.useState(null);
    const [, setIsLoading] = React.useState(false);

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegmentIsNumeric = pathSegments.length > 0 && !isNaN(+pathSegments[0]);

    let initialTab = 0;
    if (firstSegmentIsNumeric) {
        initialTab = +pathSegments[0];
    }
    
    // Local state for current active tab (allows switching even on deep paths)
    const [currentActiveTab, setCurrentActiveTab] = React.useState(initialTab);
    
    let intendedPath = null;
    // If the first segment is a number (e.g., /1/trip/2357)
    if (firstSegmentIsNumeric) {
        // And if there are more segments after it, that's our intended path
        if (pathSegments.length > 1) {
            intendedPath = `/${pathSegments.slice(1).join('/')}${location.search}`;
        }
        // If the path is just /1, intendedPath remains null, so user goes to dashboard
    } else if (location.pathname !== '/') {
        // For non-numeric paths like /trip/2357
        intendedPath = location.pathname + location.search;
    }
    
    const ownURL = `${window.location.origin}${location.pathname}`;
    const loginHint = loginHintFrom(location.search);

    const auth_methods = initData && initData.auth_methods ? initData.auth_methods : [];
    if (auth_methods.length === 0) {
        auth_methods.push({ backend: 'local' });
    }

    const validCurrentTab = currentActiveTab < auth_methods.length ? currentActiveTab : 0;

    const onCurrentTabChange = (ev, newTab) => {
        setCurrentActiveTab(newTab);
        
        // Only change URL for simple auth selection paths like /0, /1, etc.
        const isSimpleAuthPath = pathSegments.length === 0 || (pathSegments.length === 1 && firstSegmentIsNumeric);
        if (isSimpleAuthPath) {
            history.push(`/${newTab}`);
        }
        // For deep paths like /1/trip/2357, we don't change the URL but allow tab switching
    };

    console.log('auth0:initData', validCurrentTab, location, intendedPath);

    React.useEffect(() => {
        if (initData !== null) { return; }
        setIsLoading(true);
        let active = true;
        let tm = null;
        let delay = 1;
        const fetchInitData = async () => {
            const host = config.domainOverride || window.location.host;
            try {
                const res = await fetch(`${config.apiUrl}/login/?host=${host}`);
                // console.log('auth0:fetchInitData', active, res);
                if (!active) { return; }
                if (res.status === 200) {
                    setInitError(null);
                    setInitData(await res.json());
                    clearTimeout(tm);
                } else {
                    // A closed tenant is final: its words, and no retry.
                    const closed = res.status === 403 ? await tenantClosedText(res) : null;
                    if (!active) { return; }
                    if (closed) {
                        setClosedText(closed);
                        return;
                    }
                    setInitData(null);
                    setInitError({ "status": res.status, "message": res.statusText });
                    delay = Math.min(delay * 2, 60);
                    tm = setTimeout(fetchInitData, delay * 1000);
                }
            } catch(err) {
                if (!active) { return; }
                setInitData(null);
                setInitError({ "message": err.message });
                delay = Math.min(delay * 2, 60);
                tm = setTimeout(fetchInitData, delay * 1000);
            }
            finally {
                setIsLoading(false);
            };
        };
        tm = setTimeout(fetchInitData, delay * 1000);
        return () => { active = false; };
    }, [initData]);

    const signInWith = (session, path = intendedPath) => finalizeLogin(session, path);
    const restart = () => setStep(null);

    let stepView = null;
    if (step?.kind === 'mfa') {
        stepView = (
            <MfaCodeStep
                pending={step.pending}
                onRestart={restart}
                onSignedIn={(session, method) => (method === 'recovery_code' && Number.isInteger(session.recovery_codes_remaining)
                    ? setStep({ kind: 'codes_left', session })
                    : signInWith(session))}
            />
        );
    } else if (step?.kind === 'enroll') {
        stepView = (
            <MfaEnrollStep
                enrollment={step.enrollment}
                onRestart={restart}
                onEnrolled={(session) => (session.recovery_codes?.length ? setStep({ kind: 'codes', session }) : signInWith(session))}
            />
        );
    } else if (step?.kind === 'codes') {
        const { session } = step;
        stepView = (
            <RecoveryCodesStep
                codes={session.recovery_codes}
                account={[session.user?.email, session.tour_operator?.name].filter(Boolean).join(' · ')}
                fileName={`touroptima-recovery-codes-${session.tour_operator?.code || 'dashboard'}.txt`}
                doneLabel="Continue"
                onDone={() => signInWith(session)}
            />
        );
    } else if (step?.kind === 'codes_left') {
        stepView = (
            <RecoveryCodesLeftStep
                remaining={step.session.recovery_codes_remaining}
                onContinue={() => signInWith(step.session)}
                onOpenSecurity={() => signInWith(step.session, ACCOUNT_SECURITY_PATH)}
            />
        );
    }

    if (closedText !== null) {
        return (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <div className={classes.paper}>
                    <Typography component="h1" variant="h5" align="center">{closedText}</Typography>
                </div>
            </Container>
        );
    }
    if (!initData) {
        return (<Container component="main" maxWidth="xs">
            <LinearProgress color="primary" style={{position: 'fixed', zIndex: 1, top: 0, left: 0, right: 0 }} />
            <CssBaseline />
            <div className={classes.paper}>
                Loading, please wait...
            </div>
        </Container>);
    }
    return (
        <Container component="main" maxWidth="xs">
            <LinearProgress color="primary" style={{ visibility: loading ? 'visible' : 'hidden', position: 'fixed', zIndex: 1, top: 0, left: 0, right: 0 }} />
            <CssBaseline />
            <div className={classes.paper}>
                { initData.logo && initData.logo.url ? <>
                    <Image
                        src={initData.logo.url}
                        fit={"contain"}
                        duration={1000}
                        width={128}
                    />
                </> : <>
                    <Avatar className={classes.avatar}>
                        <LockOutlinedIcon />
                    </Avatar>
                </> }
                <Typography component="h1" variant="h5">
                    {initData.name}: Sign In
                </Typography>
                { signOutReason && !stepView && <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>{signOutReason}</Alert> }
                { stepView && <div className={classes.tabPanel}>{stepView}</div> }
                { !stepView && auth_methods.length > 1 && <>
                    <Tabs value={validCurrentTab} onChange={onCurrentTabChange} >
                        { auth_methods.map((auth_method, idx, lst) => (
                            <Tab label={auth_method.name || auth_method.backend} key={idx} {...a11yProps(idx)} />
                        )) }
                    </Tabs>
                </> }

                { !stepView && auth_methods.map((auth_method, idx, lst) => (
                    <TabPanel value={validCurrentTab} index={idx} key={idx} className={classes.tabPanel}>
                        {/* Note: if Auth0Provider is moved into the AuthMethod component, auth0 initialization never happens*/}
                        {auth_method.backend === "auth0" ? (
                            <Auth0Provider 
                                    domain={auth_method.domain} 
                                    clientId={auth_method.client_id} 
                                    authorizationParams={{ redirect_uri: ownURL }}>
                                <AuthMethod data={auth_method} key={idx} idx={idx} count={lst.length} intendedPath={intendedPath}/>
                            </Auth0Provider>
                        ) : (
                            <AuthMethod data={auth_method} key={idx} idx={idx} count={lst.length} intendedPath={intendedPath} onSecondStep={setStep} loginHint={loginHint}/>
                        )}
                    </TabPanel>
                )) }
            </div>
            <Box mt={8}>
                <Copyright />
            </Box>
        </Container>
    );
}

export default SignIn;
